import type React from 'react';

import type { IOrganizationUserDto } from '@treneo/models';
import { useAppTranslations } from '@treneo/shared/i18n';
import { PersonAddIcon } from '@treneo/shared/icons';
import { Spacing } from '@treneo/shared/themes';
import {
  Autocomplete,
  BUTTON_SIZES,
  BUTTON_VARIANTS,
  Card,
  CardContent,
  LoadingButton,
  Row,
  TextField
} from '@treneo/shared/ui';

import { useInviteTrainerMutation } from '../../data';
import { useInviteTrainerForm } from '../../hooks/use-invite-trainer-form';
import { handleMutationError } from '../../utils/error-handling.utils';

type Props = Readonly<{
  organizationId: string;
  users: IOrganizationUserDto[];
  onInviteSuccess: () => void;
}>;

export const InviteTrainerInlineInput = ({ organizationId, users, onInviteSuccess }: Props) => {
  const { t } = useAppTranslations();
  const inviteTrainerMutation = useInviteTrainerMutation();

  const {
    email,
    selectedUser,
    isSubmitting,
    nonTrainerUsers,
    getOptionLabel,
    handleSubmit: handleFormSubmit,
    handleInputChange,
    handleValueChange,
    isDisabled
  } = useInviteTrainerForm({
    t,
    organizationId,
    users,
    onInvite: async (emailToInvite: string) => {
      await inviteTrainerMutation.mutateAsync({
        organizationId,
        email: emailToInvite,
        origin: window.location.origin
      });
    },
    onSuccess: onInviteSuccess
  });

  const handleSubmit = async () => {
    try {
      await handleFormSubmit();
    } catch (error: unknown) {
      handleMutationError(error, 'trainers.inviteError', t);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Row alignItems='center' sx={{ gap: Spacing.small }}>
          <Autocomplete
            freeSolo
            options={nonTrainerUsers}
            value={selectedUser}
            inputValue={email}
            onInputChange={(_, newInputValue) => handleInputChange(newInputValue)}
            onChange={(_, newValue) => handleValueChange(newValue)}
            getOptionLabel={getOptionLabel}
            renderInput={params => (
              <TextField
                {...params}
                label={t('trainers.email')}
                placeholder={t('trainers.enterEmailOrSelectUser')}
                onKeyDown={handleKeyDown}
                fullWidth
                disabled={isSubmitting}
                size='small'
                slotProps={{ inputLabel: { shrink: true } }}
              />
            )}
            sx={{ flex: 1 }}
            disabled={isSubmitting}
          />
          <LoadingButton
            variant={BUTTON_VARIANTS.contained}
            startIcon={<PersonAddIcon />}
            onClick={handleSubmit}
            disabled={isSubmitting || isDisabled}
            size={BUTTON_SIZES.small}
            isLoading={isSubmitting}
            loadingText={t('common.sending')}
          >
            {t('trainers.sendInvitation')}
          </LoadingButton>
        </Row>
      </CardContent>
    </Card>
  );
};
